import settings from "@/settings";
import axios from "axios";

const AUTH_STORAGE_KEY = "auth_code";

/**
 * Authentication against the GeoServer instance that publishes the layers.
 * Credentials are verified with HTTP Basic and the resulting token is kept in
 * sessionStorage, so the session survives a page refresh but not the closing of the tab.
 */
class AuthServices {
  private encodeCredentials(username: string, password: string): string {
    const bytes = new TextEncoder().encode(`${username}:${password}`);
    let binary = "";
    bytes.forEach(byte => (binary += String.fromCharCode(byte)));
    return window.btoa(binary);
  }

  private decodeUsername(token: string): string | null {
    try {
      const binary = window.atob(token);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      return new TextDecoder().decode(bytes).split(":")[0];
    } catch {
      return null;
    }
  }

  private async verify(token: string): Promise<boolean> {
    if (!token) {
      return false;
    }

    try {
      const query = await axios.get(settings.geoServerCredentialCheckUrl, {
        headers: { Authorization: `Basic ${token}` },
      });
      return query.status === 200;
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return sessionStorage.getItem(AUTH_STORAGE_KEY);
  }

  getAuthorizationHeader(): Record<string, string> {
    const token = this.getToken();
    return token ? { Authorization: `Basic ${token}` } : {};
  }

  getUsername(): string | null {
    const token = this.getToken();
    return token ? this.decodeUsername(token) : null;
  }

  async isAuthorized(): Promise<boolean> {
    const authorized = await this.verify(this.getToken());
    if (!authorized) {
      this.logout();
    }
    return authorized;
  }

  async login(username: string, password: string): Promise<boolean> {
    const token = this.encodeCredentials(username, password);

    if (!(await this.verify(token))) {
      this.logout();
      return false;
    }

    sessionStorage.setItem(AUTH_STORAGE_KEY, token);
    return true;
  }

  logout(): void {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

export const authService = new AuthServices();
